import { useState } from "react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { useFormattedPhone } from "@/lib/usePhone";
import SmsConsent from "@/components/SmsConsent";
import { BUSINESS } from "@/data/legal";

export default function ChimeIn() {
  const [form, setForm] = useState({
    name: "", email: "", subject: "", message: "", sms_opt_in: false,
  });
  const phone = useFormattedPhone();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const phoneEntered = phone.hasValue;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required.");
      return;
    }
    if (phoneEntered && !phone.isValid) {
      toast.error(phone.error);
      return;
    }
    if (phoneEntered && !form.sms_opt_in) {
      toast.error("Please check the SMS consent box to receive texts.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        subject: form.subject || undefined,
        message: form.message,
        phone: phone.normalized || undefined,
        sms_opt_in: !!form.sms_opt_in,
      });
      setSent(true);
      toast.success("Your note has been delivered to The House. We'll be in touch soon.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5">
          <p className="label-eyebrow">Chime In</p>
          <h1 className="font-display text-bone text-5xl md:text-6xl mt-5 leading-[0.95]">
            Write to the House.
          </h1>
          <p className="mt-6 text-bone/65 max-w-md">
            Bookings, private residencies, press, or just a question. We answer every letter in the order written.
          </p>

          <div className="mt-10 text-bone/75">
            <p className="label-eyebrow">Press · Private Events · Stewards</p>
            <a
              href={`mailto:${BUSINESS.email}`}
              data-testid="contact-email"
              className="font-display text-bone text-2xl md:text-3xl mt-3 inline-block hover:text-brass transition-colors"
            >
              {BUSINESS.email}
            </a>
            <p className="text-bone/45 text-sm mt-3 max-w-md">
              One address. Mark your subject — Press, Private Event, or Stewards — and we&apos;ll route it from there.
            </p>
          </div>

          <div className="mt-10 border border-bone/10 p-6 md:p-7 relative overflow-hidden">
            <p className="label-eyebrow text-bone/60">Operated by</p>
            <p className="font-display text-bone text-2xl md:text-[26px] leading-tight mt-3">
              The Uncanny House <span className="text-brass">LLC</span>
            </p>
            <div className="mt-4 hairline opacity-60" />
            <address
              data-testid="operated-by-address"
              className="not-italic mt-5 text-bone/75 text-[15px] leading-relaxed"
            >
              <p>2108 N St, Ste N</p>
              <p>Sacramento, CA 95816</p>
              <p className="mt-4">
                <a
                  data-testid="operated-by-phone"
                  href="tel:+15592366336"
                  className="font-display text-bone text-xl md:text-2xl hover:text-brass transition-colors"
                >
                  (559) 236-6336
                </a>
              </p>
              <p data-testid="operated-by-sms-consent" className="mt-3 text-lavender text-[12px] leading-relaxed">
                By contacting us via SMS with your mobile number, you consent to receive text messages
                from The Uncanny House LLC. Msg&amp;Data rates may apply. Reply &ldquo;STOP&rdquo; at any time to opt out.
              </p>
            </address>
          </div>
        </div>

        <div className="md:col-span-7">
          {sent ? (
            <div data-testid="contact-success" className="border border-brass/40 bg-espresso/20 p-10">
              <p className="label-eyebrow text-brass">Received</p>
              <h2 className="font-display text-bone text-4xl mt-4">
                Thank you, {form.name.split(" ")[0]}.
              </h2>
              <p className="mt-4 text-bone/70 max-w-md">
                We&apos;ll respond by hand. If your matter is time-sensitive, please mark it so in the subject.
              </p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-7">
              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Name</label>
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Email</label>
                  <input
                    data-testid="contact-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                    placeholder="you@somewhere.house"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">
                    Mobile Number <span className="text-bone/35">(optional)</span>
                  </label>
                  <input
                    data-testid="contact-input-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={phone.display}
                    onChange={phone.onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                    placeholder="1 (555) 555-5555"
                  />
                  {phoneEntered && !phone.isValid && (
                    <p className="mt-2 text-[11px] text-red-300/85">{phone.error}</p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Subject</label>
                  <input
                    data-testid="contact-input-subject"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-xl"
                    placeholder="Press, Private Event, etc."
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Message</label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  className="w-full bg-transparent border border-bone/15 focus:border-brass outline-none p-4 text-bone placeholder-bone/30 leading-relaxed"
                  placeholder="Tell us what you have in mind."
                />
              </div>

              <SmsConsent
                testId="contact-sms-consent"
                checked={form.sms_opt_in}
                onChange={(v) => setForm((f) => ({ ...f, sms_opt_in: v }))}
                required={phoneEntered}
              />

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="inline-flex items-center px-8 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send the Letter"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
